<template>
  <div class="bill-info">
    <app-state v-if="loading" state="loading" title="Загружаем счет на оплату" />
    <app-state
      v-else-if="missingPayload"
      state="not-found"
      title="Данные счета на оплату не найдены"
      message="Документ существует в реестре, но его платежные реквизиты отсутствуют."
      data-testid="bill-to-pay-missing"
    />
    <template v-else>
      <page-section title="Оплата" description="Назначение и итоговая сумма выставленного счета.">
        <div class="bill-info__summary">
          <div><span>К оплате</span><amount-cell :value="body.payment_total || body.payment_amount" /></div>
          <div><span>Без НДС</span><amount-cell :value="body.payment_without_nds" /></div>
          <div><span>Номер счета</span><strong>{{ body.in_org_number || body.payment_id || '—' }}</strong></div>
        </div>
        <app-data-table
          title="Позиции счета"
          :rows="rows"
          :columns="columns"
          :count="rows.length"
          state="ready"
          storage-key="bill-to-pay-info"
          data-testid="bill-to-pay-rows"
        >
          <template #body-cell-price="props"><q-td :props="props"><amount-cell :value="props.value" /></q-td></template>
          <template #body-cell-total="props"><q-td :props="props"><amount-cell :value="props.value" /></q-td></template>
        </app-data-table>
      </page-section>

      <div class="bill-info__parties">
        <page-section title="Поставщик">
          <dl class="bill-info__details">
            <div><dt>Организация</dt><dd>{{ body.maker_name || '—' }}</dd></div>
            <div><dt>ИНН / КПП</dt><dd>{{ joinValues(body.maker_inn, body.maker_kpp) }}</dd></div>
            <div><dt>Адрес</dt><dd>{{ body.maker_address || '—' }}</dd></div>
            <div><dt>Телефон</dt><dd>{{ body.maker_phone || '—' }}</dd></div>
          </dl>
        </page-section>
        <page-section title="Покупатель">
          <dl class="bill-info__details">
            <div><dt>Организация</dt><dd>{{ body.organisation || '—' }}</dd></div>
            <div><dt>Получатель</dt><dd>{{ body.maker_receiver || body.maker_name || '—' }}</dd></div>
          </dl>
        </page-section>
      </div>

      <page-section title="Банковские реквизиты">
        <dl class="bill-info__bank">
          <div><dt>Банк получателя</dt><dd>{{ body.maker_bank || '—' }}</dd></div>
          <div><dt>БИК</dt><dd>{{ body.maker_bank_bik || '—' }}</dd></div>
          <div><dt>Расчетный счет</dt><dd>{{ body.maker_bank_account || '—' }}</dd></div>
          <div><dt>Корреспондентский счет</dt><dd>{{ body.maker_bank_account_ext || '—' }}</dd></div>
        </dl>
      </page-section>
    </template>
  </div>
</template>

<script>
import AmountCell from '../../../../components/ui/AmountCell.vue'
import AppDataTable from '../../../../components/ui/AppDataTable.vue'
import AppState from '../../../../components/ui/AppState.vue'
import PageSection from '../../../../components/ui/PageSection.vue'

const columns = Object.freeze([
  { name: 'name', label: 'Товар или услуга', align: 'left', field: 'name', required: true },
  { name: 'quantity', label: 'Количество', align: 'right', field: 'quantity' },
  { name: 'price', label: 'Цена', align: 'right', field: 'price' },
  { name: 'total', label: 'Сумма', align: 'right', field: 'total' }
])

export default {
  name: 'document_info_bill_to_pay',
  components: { AmountCell, AppDataTable, AppState, PageSection },
  props: { embeddedWorkbench: { type: Boolean, default: false } },
  data() { return { body: {}, loading: true, infoRequestId: null, handlers: [], columns } },
  computed: {
    missingPayload() {
      if (!this.body || !Object.keys(this.body).length) return true
      return !this.body.payment_id && !this.body.payment_position_name && !this.body.payment_amount && !this.body.payment_total
    },
    rows() {
      if (Array.isArray(this.body.table_rows) && this.body.table_rows.length) {
        return this.body.table_rows.map((row, index) => ({
          id: row.id || index + 1,
          name: row.name || 'Оплата по счету',
          quantity: row.quantity || 1,
          price: row.price || row.total,
          total: row.total || row.price
        }))
      }
      return [{
        id: 1,
        name: this.body.payment_position_name || 'Оплата по счету',
        quantity: 1,
        price: this.body.payment_amount || this.body.payment_total,
        total: this.body.payment_amount || this.body.payment_total
      }]
    }
  },
  mounted() {
    this.listen('didReceiveDocumentInfo', this.onDocumentInfo)
    this.listen('connectionReady', this.loadDocument)
    if (this.connection.isAuthenticated) this.loadDocument()
  },
  beforeUnmount() { this.handlers.forEach(([event, handler]) => this.emitter.off(event, handler)) },
  methods: {
    listen(event, handler) { this.emitter.on(event, handler); this.handlers.push([event, handler]) },
    loadDocument() {
      this.loading = true
      this.infoRequestId = this.connection.makeRequestId('bill-to-pay-info')
      this.connection.getDocumentInfo(this.infoRequestId, this.$route.params.id, this.$route.params.history_id || 'last')
    },
    onDocumentInfo(response) {
      if (response.request_id !== this.infoRequestId || response.data.document_type !== 'bill_to_pay') return
      this.body = response.data.body || {}
      this.loading = false
    },
    joinValues(first, second) { return [first, second].filter(Boolean).join(' / ') || '—' }
  }
}
</script>

<style scoped>
.bill-info { display: grid; gap: var(--sid-space-6); min-width: 0; }
.bill-info__summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--sid-space-4); margin-bottom: var(--sid-space-5); }
.bill-info__summary div { display: grid; gap: var(--sid-space-1); min-width: 0; }
.bill-info__summary span, .bill-info dt { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.bill-info__parties { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-6); }
.bill-info__details, .bill-info__bank { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-4); margin: 0; }
.bill-info__details div, .bill-info__bank div { min-width: 0; }
.bill-info dd { margin: var(--sid-space-1) 0 0; overflow-wrap: anywhere; }
@media (max-width: 899px) { .bill-info__parties { grid-template-columns: 1fr; } }
@media (max-width: 599px) { .bill-info__summary, .bill-info__details, .bill-info__bank { grid-template-columns: 1fr; } }
</style>
